class ErrorLogger {
  constructor() {
    this.logFile = 'error_logs.txt';
    this.maxLogSize = 5 * 1024 * 1024;
    this.maxLogEntries = 1000;
  }

  formatTimestamp() {
    const now = new Date();
    return now.toISOString();
  }

  sanitizeMessage(message) {
    if (!message) return 'Unknown error';

    if (message.length > 200) {
      if (message.includes('className') || message.includes('class=')) {
        return 'CSS class error';
      }
      return message.substring(0, 50) + '...';
    }

    return message
      .replace(/[^\w\s\-_.,!?()[\]{}:;@#$%^&*+=<>]/g, '')
      .replace(/\s+/g, ' ')
      .substring(0, 100);
  }

  createLogEntry(error, context = {}) {
    const timestamp = this.formatTimestamp();
    const sanitizedMessage = this.sanitizeMessage(error.message || error.toString());

    const logEntry = {
      timestamp,
      type: error.name || 'Error',
      message: sanitizedMessage,
      stack: error.stack || '',
      context: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...context,
      },
    };

    return JSON.stringify(logEntry, null, 2);
  }

  async writeToFile(logEntry) {
    try {
      if (this.hasUserInteracted() && 'showSaveFilePicker' in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: this.logFile,
            types: [
              {
                description: 'Text Files',
                accept: { 'text/plain': ['.txt'] },
              },
            ],
          });

          const writable = await handle.createWritable();
          await writable.write(logEntry + '\n\n');
          await writable.close();
        } catch (fileError) {
          this.fallbackLogging(logEntry);
        }
      } else {
        this.fallbackLogging(logEntry);
      }
    } catch (error) {
      this.fallbackLogging(logEntry);
    }
  }

  hasUserInteracted() {
    return window.userHasInteracted || false;
  }

  setUserInteracted() {
    window.userHasInteracted = true;
  }

  // Fallback logging methods
  downloadLog(logEntry) {
    const blob = new Blob([logEntry + '\n\n'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error_log_${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  fallbackLogging(logEntry) {
    try {
      const existingLogs = localStorage.getItem('errorLogs') || '[]';
      const logs = JSON.parse(existingLogs);
      logs.push(JSON.parse(logEntry));

      if (logs.length > 100) {
        logs.splice(0, logs.length - 100);
      }

      localStorage.setItem('errorLogs', JSON.stringify(logs));
    } catch (error) {}
  }

  async logError(error, context = {}) {
    try {
      if (this.shouldSkipError(error, context)) {
        return;
      }

      const logEntry = this.createLogEntry(error, context);

      await this.writeToFile(logEntry);

      this.storeInMemory(logEntry);
    } catch (loggingError) {}
  }
  shouldSkipError(error, context = {}) {
    const errorMessage = error.message || error.toString();

    if (
      errorMessage.includes('Performance: Attribute value') &&
      errorMessage.includes('is invalid')
    ) {
      return true;
    }

    if (errorMessage.includes('showSaveFilePicker') && errorMessage.includes('user gesture')) {
      return true;
    }

    if (
      errorMessage.length > 500 &&
      (errorMessage.includes('className') || errorMessage.includes('class='))
    ) {
      return true;
    }

    return false;
  }

  storeInMemory(logEntry) {
    if (!window.errorLogs) {
      window.errorLogs = [];
    }

    window.errorLogs.push(JSON.parse(logEntry));

    if (window.errorLogs.length > 50) {
      window.errorLogs.splice(0, window.errorLogs.length - 50);
    }
  }

  getStoredLogs() {
    const logs = [];

    try {
      const storedLogs = localStorage.getItem('errorLogs');
      if (storedLogs) {
        logs.push(...JSON.parse(storedLogs));
      }
    } catch (error) {}

    if (window.errorLogs) {
      logs.push(...window.errorLogs);
    }

    return logs;
  }

  clearStoredLogs() {
    try {
      localStorage.removeItem('errorLogs');
      window.errorLogs = [];
    } catch (error) {}
  }

  exportLogs() {
    const logs = this.getStoredLogs();
    const logText = logs.map(log => JSON.stringify(log, null, 2)).join('\n\n');
    this.downloadLog(logText);
  }
}

const errorLogger = new ErrorLogger();

const errorLoggerProxy = new Proxy(errorLogger, {
  get(target, prop) {
    const value = target[prop];
    if (typeof value === 'function') {
      return value.bind(target);
    }
    return value;
  },
});

window.addEventListener('error', event => {
  errorLoggerProxy.logError(event.error || new Error(event.message), {
    type: 'unhandled',
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

window.addEventListener('unhandledrejection', event => {
  errorLoggerProxy.logError(new Error(event.reason), {
    type: 'unhandledrejection',
    promise: event.promise,
  });
});

const handleFirebasePerformanceError = error => {
  errorLoggerProxy.logError(error, {
    type: 'firebase_performance',
    component: 'Firebase Performance',
    action: 'trace_creation',
  });
};

export default errorLoggerProxy;
export { handleFirebasePerformanceError };
